import {
  revertedWordLibraryToolCallIdsAtom,
  wordLibraryPreviousValuesByToolCallIdAtom,
} from '@/atoms/chatAtoms';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import {
  TypographyCaption,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { useMutateUpdateSettings } from '@/hooks/mutation/useMutateUpdateSettings';
import { useQueryUser } from '@heidi/workspace/data';
import { useInvokeOnce } from '@heidi/utils';
import { cn } from '@/lib/cn';
import { useAtom } from 'jotai';
import { AlertCircle, ArrowRight, BookType, Loader2, Undo2 } from 'lucide-react';
import { useEffect, type JSX } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface WordLibraryEntry {
  from: string;
  to: string;
}

/** Prior value per key; `null` means the key was absent before the tool ran. */
export type WordLibraryPreviousValues = Record<string, string | null>;

interface UpdateWordLibraryBlockProps {
  status: 'applying' | 'completed' | 'error';
  /** Applied find→replace pairs from the tool input. */
  entries?: WordLibraryEntry[];
  added?: number;
  updated?: number;
  /**
   * Optional prior values from the tool result (or a live applying snapshot).
   * When omitted, the block snapshots from the user library while applying.
   */
  previousValues?: WordLibraryPreviousValues;
  error?: string;
  onBlockError: (blockType: 'update_word_library', error?: string) => void;
  /** Stable tool-call id, used to persist the terminal "reverted" state. */
  toolId?: string;
  className?: string;
}

// Cap the persisted reverted-id map; prune oldest past this on write.
const MAX_REVERTED_TOOL_CALL_IDS = 200;

type RevertedMap = Record<string, true>;

const writeReverted = (ids: RevertedMap, toolId: string): RevertedMap => {
  const next: RevertedMap = { ...ids, [toolId]: true };
  const keys = Object.keys(next);
  if (keys.length > MAX_REVERTED_TOOL_CALL_IDS) {
    for (const stale of keys.slice(0, keys.length - MAX_REVERTED_TOOL_CALL_IDS)) {
      delete next[stale];
    }
  }
  return next;
};

const unlockReverted = (ids: RevertedMap, toolId: string): RevertedMap => {
  const next: RevertedMap = { ...ids };
  delete next[toolId];
  return next;
};

const snapshotPreviousValues = (
  entries: WordLibraryEntry[],
  library: Record<string, string>
): WordLibraryPreviousValues => {
  const snap: WordLibraryPreviousValues = {};
  for (const entry of entries) {
    // Indexed access is `string | undefined`; treat missing keys as `null`.
    snap[entry.from] = library[entry.from] ?? null;
  }
  return snap;
};

const applyRevertToLibrary = (
  library: Record<string, string>,
  entries: WordLibraryEntry[],
  previous: WordLibraryPreviousValues | undefined
): Record<string, string> => {
  const nextLibrary = { ...library };
  for (const entry of entries) {
    if (previous && Object.prototype.hasOwnProperty.call(previous, entry.from)) {
      const prior = previous[entry.from];
      if (typeof prior === 'string') {
        nextLibrary[entry.from] = prior;
      } else {
        // `null` (or unexpected undefined) — key was absent before the tool ran.
        delete nextLibrary[entry.from];
      }
      continue;
    }
    // No prior snapshot (e.g. history without an applying pass) — remove the key.
    delete nextLibrary[entry.from];
  }
  return nextLibrary;
};

const summaryMessage = (added: number, updated: number) => {
  if (added > 0 && updated > 0) {
    return (
      <FormattedMessage
        defaultMessage="{added, plural, one {# entry added} other {# entries added}}, {updated, plural, one {# updated} other {# updated}}"
        id="MYkzj90SbS"
        values={{ added, updated }}
      />
    );
  }
  if (updated > 0) {
    return (
      <FormattedMessage
        defaultMessage="{count, plural, one {# entry updated} other {# entries updated}}"
        id="EFow0WRAry"
        values={{ count: updated }}
      />
    );
  }
  return (
    <FormattedMessage
      defaultMessage="{count, plural, one {# entry added} other {# entries added}}"
      id="6ZEACWV9Qr"
      values={{ count: added }}
    />
  );
};

/**
 * Renders an agent `update_word_library` tool call. On success it shows the
 * applied find→replace chips and a Revert action that restores prior values for
 * updated keys and removes keys that were newly added.
 */
export const UpdateWordLibraryBlock = ({
  status,
  entries = [],
  added = 0,
  updated = 0,
  previousValues: previousValuesProp,
  error,
  onBlockError,
  toolId,
  className,
}: UpdateWordLibraryBlockProps): JSX.Element => {
  const intl = useIntl();
  useInvokeOnce(
    () => onBlockError('update_word_library', error),
    status === 'error'
  );
  const { mutateAsync: updateSettings, isPending: isReverting } =
    useMutateUpdateSettings({ showToast: false });
  const { data: user } = useQueryUser((data) => ({
    user_word_library: data?.session_settings?.user_word_library,
  }));
  const [revertedToolCallIds, setRevertedToolCallIds] = useAtom(
    revertedWordLibraryToolCallIdsAtom
  );
  const [previousByToolCallId, setPreviousByToolCallId] = useAtom(
    wordLibraryPreviousValuesByToolCallIdAtom
  );
  const isReverted = toolId ? revertedToolCallIds[toolId] === true : false;

  // Capture prior values while applying — the BE write has not landed yet.
  useEffect(() => {
    if (status !== 'applying' || !toolId || entries.length === 0) return;
    setPreviousByToolCallId((prev) => {
      if (prev[toolId]) return prev;
      return {
        ...prev,
        [toolId]: snapshotPreviousValues(entries, user?.user_word_library ?? {}),
      };
    });
  }, [status, toolId, entries, user?.user_word_library, setPreviousByToolCallId]);

  if (status === 'applying') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="update-word-library-block"
        data-status="applying"
      >
        <BookType
          className="size-5 shrink-0 text-text-tertiary"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="text-text-primary">
            <FormattedMessage defaultMessage="Word library" id="f1dYJPZ+zc" />
          </TypographyPBold>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="text-text-tertiary">
              <FormattedMessage
                defaultMessage="Updating word library..."
                id="o+h51V/3xQ"
              />
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="update-word-library-block"
        data-status="error"
      >
        <AlertCircle
          className="size-5 shrink-0 text-validation-destructive"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="text-text-primary">
            <FormattedMessage
              defaultMessage="Couldn't update word library"
              id="oFtkD436aF"
            />
          </TypographyPBold>
          <TypographyCaption className="text-validation-destructive">
            {error ?? (
              <FormattedMessage
                defaultMessage="Something went wrong while updating the word library."
                id="fzoipSFiwt"
              />
            )}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  if (isReverted) {
    return (
      <div
        className={cn(
          'flex w-full flex-col gap-2 rounded-xl border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="update-word-library-block"
        data-status="reverted"
      >
        <TypographyPBold className="text-text-disabled line-through">
          <FormattedMessage
            defaultMessage="Word library updated"
            id="umjmILLJeW"
          />
        </TypographyPBold>
        <TypographyP2Bold className="text-text-secondary">
          <FormattedMessage defaultMessage="Reverted" id="h6qopMZfNT" />
        </TypographyP2Bold>
      </div>
    );
  }

  const canRevert = Boolean(toolId) && entries.length > 0 && !isReverting;
  const previousValues =
    previousValuesProp ?? (toolId ? previousByToolCallId[toolId] : undefined);

  const handleRevert = async () => {
    if (!toolId || entries.length === 0) return;
    setRevertedToolCallIds((prev) => writeReverted(prev, toolId));
    try {
      const nextLibrary = applyRevertToLibrary(
        user?.user_word_library ?? {},
        entries,
        previousValues
      );
      await updateSettings({
        session_settings: {
          user_word_library: nextLibrary,
        },
      });
      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Word library changes reverted',
          id: 'Tsn4BOw99U',
        }),
      });
    } catch {
      setRevertedToolCallIds((prev) => unlockReverted(prev, toolId));
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: "Couldn't revert word library changes",
          id: 'c/5MHw3H2H',
        }),
      });
    }
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-3 rounded-xl border border-border bg-surface-accent p-4',
        'transition-all duration-200',
        className
      )}
      data-testid="update-word-library-block"
      data-status="completed"
      data-entry-count={entries.length}
    >
      <div className="flex items-start gap-3">
        <BookType
          className="mt-0.5 size-5 shrink-0 text-text-secondary"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="text-text-primary">
            <FormattedMessage
              defaultMessage="Word library updated"
              id="umjmILLJeW"
            />
          </TypographyPBold>
          <TypographyCaption className="text-text-tertiary">
            {summaryMessage(added, updated)}
            <span className="text-text-disabled">
              {' · '}
              <FormattedMessage
                defaultMessage="Takes effect on future note generation"
                id="G/Hm7rsP8d"
              />
            </span>
          </TypographyCaption>
        </div>
      </div>

      {entries.length > 0 && (
        <ul className="flex flex-col gap-1.5 ps-8">
          {entries.map((entry) => (
            <li
              key={`${entry.from}→${entry.to}`}
              className="flex flex-wrap items-center gap-1.5 text-sm"
            >
              <span className="rounded-md bg-background-secondary px-2 py-0.5 font-mono text-xs text-text-primary">
                {entry.from}
              </span>
              <ArrowRight
                className="size-3 shrink-0 text-text-tertiary"
                aria-hidden="true"
              />
              <span className="rounded-md bg-background-secondary px-2 py-0.5 font-mono text-xs text-text-primary">
                {entry.to}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3 ps-8">
        <Button
          variant="ghost"
          size="sm"
          flush="all"
          className="text-text-secondary"
          onClick={() => void handleRevert()}
          disabled={!canRevert}
        >
          <FormattedMessage defaultMessage="Revert" id="amT0GhE8ux" />
          <Undo2 className="rtl:-scale-x-100" />
        </Button>
      </div>
    </div>
  );
};
